// The selected game: one game the dashboard screens' per-game parts show,
// chosen with the game bar and kept in the address as `?game=<id>`, so going
// back, a reload and the switch between Home, Openings, Middlegame and
// Ending keep it. No `?game` (or an id no longer in the period) means the
// latest game. Pure functions; the React side is in react.ts.

/** The address parameter that names the selected game. */
export const GAME_PARAM = 'game';

/** The game the address names, from a `location.search` string; undefined without one. */
export function gameParam(search: string): string | undefined {
  return new URLSearchParams(search).get(GAME_PARAM) || undefined;
}

/**
 * The selected game's index in `ids`, the period's games newest first: the
 * requested one when it is there, otherwise 0, the latest; -1 when there are
 * no games.
 */
export function selectedIndex(ids: readonly string[], requested?: string | null): number {
  if (!ids.length) return -1;
  const i = requested ? ids.indexOf(requested) : -1;
  return i >= 0 ? i : 0;
}

/**
 * The index one step from `index` in a list newest first: `delta` -1 is the
 * newer game, +1 the older; held within the list.
 */
export function stepIndex(length: number, index: number, delta: number): number {
  if (length <= 0) return -1;
  return Math.min(length - 1, Math.max(0, index + delta));
}

/** The index of the oldest (`first`) or the latest (`last`) game in a list newest first; -1 when empty. */
export function edgeIndex(length: number, edge: 'first' | 'last'): number {
  if (length <= 0) return -1;
  return edge === 'first' ? length - 1 : 0;
}

/**
 * `path` (pathname, optional search and hash) with the selected game set,
 * or removed when `id` is undefined; every other parameter and the hash kept.
 */
export function withGame(path: string, id: string | undefined): string {
  const hashAt = path.indexOf('#');
  const hash = hashAt >= 0 ? path.slice(hashAt) : '';
  const rest = hashAt >= 0 ? path.slice(0, hashAt) : path;
  const queryAt = rest.indexOf('?');
  const pathname = queryAt >= 0 ? rest.slice(0, queryAt) : rest;
  const params = new URLSearchParams(queryAt >= 0 ? rest.slice(queryAt + 1) : '');
  if (id) params.set(GAME_PARAM, id);
  else params.delete(GAME_PARAM);
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
}
